// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import useBridgeService from '../../../hooks/queries/bridge/agreement/useBridgeService';
import useBridgeInstrumentConfigurations from '../../../hooks/queries/bridge/configuration/useBridgeInstrumentConfigurations';
import useBridgeFactories from '../../../hooks/queries/bridge/configuration/useBridgeFactories';
import { DataTable } from '../../../components/Table/DataTable';
import { AllocationFactory } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import TemplateContract from '../../../utils/TemplateContract';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import DataDisplay from '../../../components/DataDisplay';
import { AppRewardConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import useBridgeAppRewardConfigurations from '../../../hooks/queries/bridge/configuration/useBridgeAppRewardConfigurations';
import { fmt } from '../../../utils/common';

type Row = TemplateContract<InstrumentConfiguration>;
type FactoryRow = TemplateContract<AllocationFactory>;
type AppRewardRow = TemplateContract<AppRewardConfiguration>;

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      valueGetter: (params) => <PartyDisplay party={params.row.payload.provider} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'issuerRequirements',
      headerName: 'Required Credentials for Issuers',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.issuerRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'holderRequirement',
      headerName: 'Required Credentials for Holders',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.holderRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'createdEventBlob',
      headerName: 'Created Event Blob',
      valueGetter: (params) => <DataDisplay data={params.row.createdEventBlob} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'contractId',
      headerName: 'Contract ID',
      valueGetter: (params) => <DataDisplay data={params.row.contractId} />,
      flex: 150,
      filterable: false,
    },
  ];
};

const createFactoryHeaders: () => GridColDef<FactoryRow>[] = () => {
  return [
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      valueGetter: (params) => <PartyDisplay party={params.row.payload.provider} />,
      flex: 650,
      filterable: false,
    },
    {
      field: 'createdEventBlob',
      headerName: 'Created Event Blob',
      valueGetter: (params) => <DataDisplay data={params.row.createdEventBlob} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'contractId',
      headerName: 'Contract ID',
      valueGetter: (params) => <DataDisplay data={params.row.contractId} />,
      flex: 150,
      filterable: false,
    },
  ];
};

const createAppRewardHeaders: () => GridColDef<AppRewardRow>[] = () => {
  return [
    {
      field: 'provider',
      headerName: 'Provider',
      valueGetter: (params) => <PartyDisplay party={params.row.payload.provider} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'appRewardDetails',
      headerName: 'Utility Operator Weight',
      valueGetter: (params) => fmt(params.row.payload.details.operatorAppRewardBeneficiary.weight, 2),
      flex: 650,
      filterable: false,
    },
    {
      field: 'createdEventBlob',
      headerName: 'Created Event Blob',
      valueGetter: (params) => <DataDisplay data={params.row.createdEventBlob} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'contractId',
      headerName: 'Contract ID',
      valueGetter: (params) => <DataDisplay data={params.row.contractId} />,
      flex: 150,
      filterable: false,
    },
  ];
};

export const Configurations = () => {
  const configurations = useBridgeInstrumentConfigurations();
  const factories = useBridgeFactories();
  const appRewards = useBridgeAppRewardConfigurations();
  const bridgeService = useBridgeService();

  const isLoading =
    configurations.isLoading ||
    factories.isLoading ||
    appRewards.isLoading ||
    bridgeService.isLoading;
  if (isLoading) return <Loading />;

  const hasData = factories.data && configurations.data && appRewards.data && bridgeService.data;
  if (!hasData) return <Typography>No configurations</Typography>;

  return (
    <>
      <DataTable
        enableTableWrapper
        title="Instrument Configurations"
        variant="h3"
        columns={createHeaders()}
        rows={configurations.data}
        rowKey={(a) => a.contractId}
      />
      <DataTable
        enableTableWrapper
        title="Allocation Factories"
        variant="h3"
        columns={createFactoryHeaders()}
        rows={factories.data}
        rowKey={(a) => a.contractId}
      />
      <DataTable
        enableTableWrapper
        title="App Reward Configurations"
        variant="h3"
        columns={createAppRewardHeaders()}
        rows={appRewards.data}
        rowKey={(a) => a.contractId}
      />
    </>
  );
};
